/**
 * @module Charts
 */
import './style.css';
import {
  Chart as ChartJS,
  Colors,
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  TimeSeriesScale,
  Tooltip,
  Legend
} from 'chart.js';
import colorBands from './color-bands';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import AlarmsByDevice from './alarms-by-device';
import AlarmsByType from './alarms-by-type';
import { t } from '@lingui/core/macro';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm';

/**
 * @function Chart
 * @memberof module:Charts
 * @description Chart component
 * @see {@link https://www.chartjs.org/docs/latest/|charts.js}
 * @example
 * // Data model
 * {
 *   datasets: [{
 *     data: []
 *   }],
 *   labels: []
 * }
 */

const brandColors = [
  '#02b4c1',
  '#5f95e8',
  '#909af4',
  '#68068b',
  '#b21253',
  '#da291c',
  '#e28e60',
  '#e7d791',
  '#1a952c',
  '#02a567',
];

const colors = {
  grid: 'rgba(60,60,60,1)'
}

if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
  Object.assign(colors, {
    grid: 'rgba(220,220,220,1)'
  })
}

ChartJS.defaults.color = '#aaa';
ChartJS.defaults.borderWidth = 0;
ChartJS.defaults.datasets.doughnut.borderWidth = 0;
ChartJS.defaults.font.family = ['-apple-system', 'BlinkMacSystemFont', 'avenir next', 'avenir', 'segoe ui', 'helvetica neue', 'helvetica', 'Cantarell', 'Ubuntu', 'roboto', 'noto', 'arial', 'sans-serif']

const defaultOptions = {
  bar: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      x: {
        grid: {
          color: colors.grid
        }
      },
      y: {
        grid: {
          color: colors.grid
        }
      }
    }
  },
  doughnut: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'left',
        labels: {
          boxWidth: 12,
          boxHeight: 12
        }
      }
    }
  },
  line: {
    // pointBackgroundColor: brandColors[0],
    // backgroundColor: brandColors[0],
    pointRadius: 1,
    pointHoverRadius: 7,
    pointBorderRadius: 0,
    pointStyle: 'cross',
    // borderColor: brandColors[0],
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'nearest',
      axis: 'x'
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          boxHeight: 12
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: colors.grid
        }
      },
      y: {
        grid: {
          color: colors.grid
        }
      }
    }
  },
}

const dayStartFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric'
});
const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit'
});

function formatXAxisTick(value) {
  const tickDate = new Date(Number(value));
  if (Number.isNaN(tickDate.getTime())) return '';

  // Day boundaries are called out with a month/day label for easier scanning.
  if (tickDate.getHours() === 0 && tickDate.getMinutes() === 0) {
    return dayStartFormatter.format(tickDate);
  }

  return timeFormatter.format(tickDate);
}
/**
 * @function getTimeScaleOptions
 * @returns Returns label and formatting options for time-based x-axis
 */
function getTimeScaleOptions() {
  return {
    type: 'time',
    ticks: {
      major: { enabled: true },
      callback: (value) => formatXAxisTick(value)
    }
  };
}

/**
 * @function bootstrapChartData
 * @returns Chart data model
 */
function bootstrapChartData () {
  return {
    datasets: [{
      label: t`Loading`,
      data: []
    }],
    labels: []
  }
}

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  TimeSeriesScale,
  LineElement,
  PointElement,
  Colors,
  Tooltip,
  Legend,
  colorBands,
  zoomPlugin
);

export {
  bootstrapChartData,
  brandColors,
  defaultOptions,
  getTimeScaleOptions,
  AlarmsByDevice,
  AlarmsByType,
  Bar,
  Doughnut,
  Line
};
